import { ChangeDetectionStrategy, Component, OnInit, inject, input } from '@angular/core';
import { Status } from '@df/core/status';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { NProductConfiguration } from '@df/module-product-configuration/df-module-product-configuration.interfaces';
import { ProductConfigurationConfigurable } from '@df/module-product-configuration/model/configurable/product-configuration-configurable';
import { ProductConfiguration } from '@df/module-product-configuration/model/product-configuration';
import { BasketService } from '@df/session/api/basket/basket.service';
import { UiButtonDirective } from '@df/ui/button/button.directive';
import { ClickEventDirective } from '@df/ui/common/click-event.directive';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { TranslationService } from '@df/ui/translation/translation.service';
import { UiComponent } from '@df/ui/ui.component';
import { Nexus } from '@tomandco/nexus-api-types';
import { ProductConfigurationBaseServiceDecorator } from '../../../../../module/product-configuration/model/base/product-configuration-base.service.decorator';
import { ActionComponent } from '../../../../../ui/api/action.component';
import { ResultComponent } from '../../../../../ui/api/result.component';
import { ProductLayoutComponent } from '../product-layout.component';

@Component({
    selector: 'product-layout-add-to-basket',
    templateUrl: './product-layout-add-to-basket.component.html',
    styleUrl: './product-layout-add-to-basket.component.scss',
    imports: [ActionComponent, ClickEventDirective, ResultComponent, TranslatePipe, UiButtonDirective],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductLayoutAddToBasketComponent extends UiComponent implements OnInit {
    readonly productViewLayout = input.required<ProductLayoutComponent>();
    readonly panel = input<boolean>(false);
    readonly modal = input<boolean>(false);
    readonly openBackInStockModal = input<boolean>(true);
    readonly showPalette = input<boolean>(false);

    /**
     * Button status
     */
    protected status = new Status();

    protected isDisabled = false;
    protected buttonCopy = '';
    protected warning?: string;

    private readonly productConfiguration = inject(ProductConfiguration) as ProductConfigurationConfigurable;
    private readonly translationService = inject(TranslationService);
    private readonly scrollToService = inject(ScrollToService);
    private readonly basketService = inject(BasketService);
    private readonly baseService = inject(ProductConfiguration).baseService as ProductConfigurationBaseServiceDecorator;

    get isSalable(): boolean {
        return (
            this.productConfiguration.isSalable() &&
            !this.productConfiguration.validate()?.find(error => error.code === NProductConfiguration.Error.Code.outOfStock)
        );
    }

    get isOutOfStock(): boolean {
        if (this.hasOnlyColorOption) {
            return !this.isSalable || this.allOutOfStock;
        }

        if (!this.modal() && (this.client.isS || this.client.isM)) {
            return this.allOutOfStock;
        }

        return (!this.isDisabled && !this.isSalable) || this.allOutOfStock;
    }

    get hasColorOption(): boolean {
        const options = this.productConfiguration.options$?.value?.find(option => option.attribute.id === 'color')?.values ?? [];

        return options.length > 1;
    }

    get hasOnlyColorOption(): boolean {
        return this.hasColorOption && !this.hasSizeOption && !this.hasLetterOption;
    }

    get hasSizeOption(): boolean {
        return !!this.productConfiguration.options$?.value?.find(option => option.attribute.id === 'size')?.values.length;
    }

    get hasLetterOption(): boolean {
        return !!this.productConfiguration.options$?.value?.find(option => option.attribute.id === 'letter')?.values.length;
    }

    get hasPalette(): boolean {
        if (!this.showPalette() || this.client.isL || this.client.isX) {
            return false;
        }

        return this.hasColorOption || (!this.hasColorOption && this.hasSizeOption && this.isOutOfStock);
    }

    get allOutOfStock(): boolean {
        return !!this.productConfiguration.options$?.value?.every(option => option.values.every(value => value.error === 'oos'));
    }

    get firstOptionLabel(): string | undefined {
        const options = this.productConfiguration.options$?.value.filter(option => option.attribute.id !== 'color') ?? [];

        if (options.length) {
            const firstOption = options[0];

            if (firstOption) {
                if (firstOption.attribute.id === 'letter') {
                    return this.translationService.transform('letter');
                }

                return firstOption.attribute.data?.label;
            }
        }

        return;
    }

    override ngOnInit(): void {
        this.subscriptions.add(this.status.subscribe(() => this.detectChanges()));

        this.subscriptions.add(this.productConfiguration.productRequestChange$.subscribe(() => this.onConfigurationChange()));
        this.onConfigurationChange();

        super.ngOnInit();
    }

    protected handlePaletteClick(): void {
        if (this.panel()) {
            this.scrollToService.scrollTo('#product-details');
        } else {
            this.productViewLayout().openProductLayoutConfigurableOptionsModal(true);
        }
    }

    /**
     * Add product to basket
     */
    protected async submit(): Promise<void> {
        if (this.modal() && this.productViewLayout().productConfigurableOptionsModalIsPalette && !this.getTempStockWarning()) {
            this.productViewLayout().closeProductLayoutConfigurableOptionsModal();

            return;
        }

        if (this.getConfigurationError()) {
            if (this.panel() && (this.client.isL || this.client.isX)) {
                this.scrollToService.scrollTo('#product-details');

                return;
            } else if (!this.hasOnlyColorOption) {
                if (!this.productViewLayout().isConfigurableOptionsModalOpen && (this.client.isS || this.client.isM)) {
                    this.productViewLayout().openProductLayoutConfigurableOptionsModal();
                } else {
                    this.status.error = this.getConfigurationError();
                }

                return;
            }
        }

        if (this.getTempStockWarning()) {
            this.productViewLayout().openStockNotification();

            return;
        }

        try {
            await this.addToBasket(this.status);
        } catch {
            // skip silently, we will display error using status
        }
    }

    /**
     * Add product in current configuration to basket
     */
    private async addToBasket(status: Status | undefined): Promise<void> {
        const additionalProducts = this.baseService.additionalProducts$.value;

        if (additionalProducts.length) {
            let request = { ...this.baseService.toBasketRequest() } as
                | Nexus.Lib.Basket.BasketRequestData
                | Nexus.Lib.Basket.BasketRequestData[];

            if (!Array.isArray(request)) {
                request = [request];
            }

            request.push(...additionalProducts);

            try {
                await this.productConfiguration.submitBefore$.dispatch(request[0]);
            } catch (error) {
                if (status) {
                    status.error = error as unknown as string;
                    return;
                }

                throw error;
            }

            await this.basketService.add(status, request, () => {
                this.baseService.additionalProducts$.next([]);
            });
        } else {
            await this.productConfiguration.addToBasket(status);
        }
    }

    /**
     * Gets a warning for temp OOS problem
     */
    private getTempStockWarning(): string | undefined {
        if (!this.isSalable || this.allOutOfStock) {
            return 'Out of stock - notify me';
        }

        return undefined;
    }

    private onConfigurationChange(): void {
        const error = this.getConfigurationError();
        this.status.reset();

        if (this.modal() && this.productViewLayout().productConfigurableOptionsModalIsPalette) {
            this.buttonCopy = this.translationService.transform('Select colour');
        } else {
            this.buttonCopy = error || this.translationService.transform('Add to bag');
        }

        this.isDisabled = !!error;
        this.warning = this.getWarning();
        this.detectChanges();
    }

    /**
     * Retrieves under button warning
     */
    private getWarning(): string | undefined {
        const maxQty = this.productConfiguration.maxQty();

        if (maxQty > 0 && (maxQty < 0 || !this.productConfiguration.isSalable())) {
            return this.translationService.transform('Only % left', maxQty);
        }

        return undefined;
    }

    private getConfigurationError(): string | undefined {
        let errorMsg!: string;

        this.productConfiguration.validate()?.find(error => {
            switch (error.code) {
                case NProductConfiguration.Error.Code.invalid:
                    errorMsg = this.translationService.transform('Option % is not valid', error.fieldName.toLowerCase());
                    return true;
                case NProductConfiguration.Error.Code.missing: {
                    const fieldName = error.fieldName.toLowerCase();

                    if (fieldName.includes('letter')) {
                        errorMsg = this.translationService.transform('Select your letter');
                    } else {
                        errorMsg = this.translationService.transform('Select your %', fieldName);
                    }

                    return true;
                }
            }
        });

        // if product is out of stock on size S and M
        // and there is no size or letter option
        if (!this.modal() && (this.client.isS || this.client.isM)) {
            if (!this.isSalable) {
                if (this.hasSizeOption && this.hasLetterOption) {
                    errorMsg = this.translationService.transform('Select your size & letter');
                } else if (this.hasSizeOption && !this.hasLetterOption) {
                    errorMsg = this.translationService.transform('Select your size');
                } else if (!this.hasSizeOption && this.hasLetterOption) {
                    errorMsg = this.translationService.transform('Select your letter');
                } else if (this.hasColorOption) {
                    errorMsg = this.translationService.transform('Select your color');
                }
            } else if (this.productConfiguration.parent$?.value?.isConfigurable && this.firstOptionLabel && !this.hasOnlyColorOption) {
                errorMsg = this.translationService.transform('Select your %', this.firstOptionLabel.toLowerCase());
            }
        }

        return errorMsg;
    }
}
